import { useState, useEffect, useRef } from 'react';

import { TMDB_API_BASE_URL, LANGUAGE } from '../constants';
import type { LibraryCollection, LibraryItem, MediaPreview, MediaRef, MediaType, TMDBPaginatedResponse } from '../types';

import { toPreview } from '../utils/media';

import { LoadingSpinner } from '../components/LoadingSpinner';
import { Pagination } from '../components/Pagination';
import { MediaGrid } from '../components/MediaGrid';

import styles from './SearchView.module.css';

interface SearchViewProps {
    apiKey: string;
    activeTab: MediaType;
    searchQuery: string;
    currentPage: number;
    updateParams: (newParams: Record<string, string | null>) => void;
    library: LibraryItem[];
    updateCollection: (ref: MediaRef, target: LibraryCollection) => void;
    onOpenModal: (id: number, type: MediaType) => void;
}

export const SearchView = ({
    apiKey, activeTab, searchQuery, currentPage, updateParams,
    library, updateCollection, onOpenModal
}: SearchViewProps) => {
    const [results, setResults] = useState<MediaPreview[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [inputValue, setInputValue] = useState(searchQuery);
    const isLocalChange = useRef(false);

    useEffect(() => {
        if (!isLocalChange.current) {
            setInputValue(searchQuery);
        }
        isLocalChange.current = false;
    }, [searchQuery]);

    const handleSearchQueryChange = (query: string) => {
        isLocalChange.current = true;
        setInputValue(query);

        if (!query.trim()) {
            setResults([]);
            setTotalPages(1);
            setLoading(false);
        }

        updateParams({ q: query, page: '1' });
    }

    const handlePageChange = (page: number) => {
        updateParams({ page: page.toString() });
    }

    useEffect(() => {
        if (!searchQuery.trim()) {
            setResults([]);
            setTotalPages(1);
            setLoading(false);
            return;
        }

        const triggerSearch = async () => {
            setLoading(true);
            const url = `${TMDB_API_BASE_URL}/search/${activeTab}?api_key=${apiKey}&language=${LANGUAGE}&query=${encodeURIComponent(searchQuery)}&page=${currentPage}&include_adult=false`;

            try {
                const response = await fetch(url);
                const data: TMDBPaginatedResponse = await response.json();

                setTotalPages(data.total_pages > 500 ? 500 : data.total_pages);

                const normalized = data.results.map(item => toPreview(item, activeTab));

                setResults(normalized);
            } catch (err) {
                console.error("Search error:", err);
            } finally {
                setLoading(false);
            }
        };

        let delayDebounceFn: number;

        if (results.length > 0 && searchQuery.trim() !== "") {
            triggerSearch();
        } else {
            delayDebounceFn = setTimeout(() => {
                triggerSearch();
            }, 500);
        }

        return () => {
            clearTimeout(delayDebounceFn);
        }
    }, [searchQuery, activeTab, currentPage, apiKey]);

    return (
        <div className={styles.searchView}>
            <div className={styles.searchFormContainer}>
                <div className={styles.inputWrapper}>
                    <input
                        className={styles.searchInput}
                        placeholder="Rechercher..."
                        value={inputValue}
                        onChange={(e) => handleSearchQueryChange(e.target.value)}
                        onFocus={(e) => e.target.select()}
                        autoFocus
                    />

                    {searchQuery && !loading && (
                        <button
                            className={styles.clearButton}
                            onClick={() => handleSearchQueryChange('')}
                            aria-label="Effacer la recherche"
                        >
                            ✕
                        </button>
                    )}

                    {loading && (
                        <div className={styles.searchLoader}>
                            <LoadingSpinner size='sm' />
                        </div>
                    )}
                </div>
            </div>

            <div id="results-top" />
            {results.length > 0 && (
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} loading={loading} />
            )}

            <MediaGrid
                items={results}
                loading={loading}
                activeTab={activeTab}
                library={library}
                updateCollection={updateCollection}
                onOpenModal={onOpenModal}
            />

            {results.length > 0 && (
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} loading={loading} />
            )}
        </div>
    );
};